<template>
    <div class="navigation-mobile" :class="{ 
        'menu-open': menuOpen,
        'panel-open': activePanelSide !== null,
        'left-panel-open': activePanelSide === 'left',
        'right-panel-open': activePanelSide === 'right'
    }">
        <button 
            @click.prevent.stop="emit('togglePanel', 'left')" 
            class="nav-button design-button"
            type="button"
            :class="{ 
                'active': activePanelSide === 'left',
                'circle': menuOpen || (activePanelSide !== null && activePanelSide !== 'left')
            }"
        >
            <span class="button-text" :class="{ 'hidden': menuOpen || (activePanelSide !== null && activePanelSide !== 'left') }">Design</span>
        </button>

        <button 
            @click.prevent.stop="activePanelSide !== null ? emit('closePanels') : emit('toggleMenu')" 
            class="nav-button menu-button"
            type="button"
            :class="{ 
                'active': menuOpen,
                'home-active': activePanelSide !== null
            }"
        >
            <template v-if="menuOpen">
                <div class="close">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 6 6 18M6 6l12 12"/>
                    </svg>
                </div>
            </template>
            <template v-else-if="activePanelSide !== null">
                <div class="home-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                    </svg>
                </div>
            </template>
            <template v-else>
                <div class="hamburger">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </template>
        </button>
        
        <!-- Technology button (right) -->
        <button 
            @click.prevent.stop="emit('togglePanel', 'right')" 
            class="nav-button tech-button"
            type="button"
            :class="{ 
                'active': activePanelSide === 'right',
                'circle': menuOpen || (activePanelSide !== null && activePanelSide !== 'right')
            }"
        >
            <span class="button-text" :class="{ 'hidden': menuOpen || (activePanelSide !== null && activePanelSide !== 'right') }">Technology</span>
        </button>
        
        <div class="mobile-menu" v-show="menuOpen">
            <nav>
                <ul>
                    <li class="menu-logo">
                        <router-link to="/" @click="emit('toggleMenu')">
                            <Logo :text-only="true" width="150" height="25"/>
                         </router-link >
                    </li>
                    <template v-if="strapiStore.navigation && strapiStore.navigation.length">
                        <li v-for="(link, index) in strapiStore.navigation" :key="index">
                            <a v-if="link.isExternal && link.url" :href="link.url" target="_blank" rel="noopener" @click="emit('toggleMenu')">{{ link.text }}</a>
                            <button 
                              v-else-if="link.url && isValidUrl(link.url)" 
                              @click.prevent.stop="handleNavClick(link, $event)" 
                              class="nav-link" 
                              :class="{ 'custom-active': isNewsActive(link.url) }"
                              type="button"
                            >
                              {{ link.text }}
                            </button>
                            <span v-else class="nav-link-disabled">{{ link.text || 'Invalid Link' }}</span>
                        </li>
                    </template>
                    <template v-else-if="strapiStore.loading.navigation">
                        <li><span>Loading navigation...</span></li>
                    </template>
                 
                </ul>
            </nav>
        </div>
    </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStrapiStore } from '../stores/strapi';
import Logo from './Logo.vue';

const props = defineProps({
    menuOpen: {
        type: Boolean,
        default: false
    },
    activePanelSide: {
        type: String,
        default: null
    }
});

const emit = defineEmits(['toggleMenu', 'togglePanel', 'closePanels']);

const router = useRouter();
const route = useRoute();
const strapiStore = useStrapiStore()

const isValidUrl = (url) => {
  if (!url || typeof url !== 'string') return false
  const trimmedUrl = url.trim()
  return trimmedUrl && trimmedUrl !== 'undefined' && trimmedUrl !== 'null' && trimmedUrl.length > 0
}

const isNewsActive = (linkUrl) => {
  if (!linkUrl || linkUrl !== '/news') return false
  return route.path.startsWith('/news')
}

const closeMenuAndNavigate = () => {
  emit('toggleMenu')
}

const handleNavClick = (link, event) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  
  // Validate link URL
  if (!link.url || !isValidUrl(link.url)) {
    console.warn('Invalid navigation link:', link)
    emit('toggleMenu')
    return
  }
  
  // Handle special panel routes - these automatically close the menu via handleMobilePanel
  if (link.url === '/design') {
    emit('togglePanel', 'left')
  } else if (link.url === '/technology') {
    emit('togglePanel', 'right')
  } else {
    // For other routes, close menu and navigate
    emit('toggleMenu')
    setTimeout(() => {
      router.push(link.url)
    }, 100)
  }
}

// Initialize data if needed
onMounted(async () => {
  if (!strapiStore.navigationData) {
    await strapiStore.fetchNavigation()
  }
    // iOS scroll fix
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
    let scrolling = false;
    let scrollTimeout;
    
    window.addEventListener('scroll', () => {
      if (!scrolling) {
        document.body.classList.add('ios-scrolling');
        scrolling = true;
      }
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        document.body.classList.remove('ios-scrolling');
        scrolling = false;
      }, 100);
    }, { passive: true });
  }
})
</script>

<style lang="scss" scoped>
@use '../assets/scss/variables' as *;
@use '../assets/scss/mixins' as m;
@use '../assets/scss/typography' as *;

.navigation-mobile {
    position: fixed;
    bottom: 16px;
    left: 0;
    right: 0;
    margin: 0 auto;
    width: 90%;
    max-width: 450px;
    height: 56px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    z-index: 1000;
    border-radius: 28px;
    background-color: white;
    padding: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: none;
    
    // Force single background layer
    background-clip: padding-box;
    -webkit-background-clip: padding-box;
    
    transition: max-width 0.3s ease;
    
    @include m.below-tablet-landscape {
        display: flex;
    }

    .menu-logo{
        text-align: center;
        width: 100%;
        
        svg{
            margin: 0 auto;
        }
    }

    &.menu-open {
        max-width: 300px;
        justify-content: space-between;
        gap: 0;
        
        .design-button,
        .tech-button {
            transform: translateX(0);
        }
    }
    
    &.panel-open {
        justify-content: center;
        gap: 4px;
        height: 60px!important;
    }
    
    &.left-panel-open {
        .design-button {
            flex: 1;
            max-width: 280px;
            border-radius: 24px;
        }
        
        .menu-button,
        .tech-button {
            flex-shrink: 0;
        }
    }
    
    &.right-panel-open {
        .tech-button {
            flex: 1;
            max-width: 280px;
            border-radius: 24px;
        }
        
        .menu-button,
        .design-button {
            flex-shrink: 0;
        }
    }
}

.nav-button {
    @extend %body-text;
    @extend %font-semibold;
    height: 48px;
    border: none;
    border-radius: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    -webkit-tap-highlight-color: transparent;
    flex-shrink: 0;
    
    // iOS optimisations
    -webkit-transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    
    &:active {
        transform: scale(0.96);
        -webkit-transform: scale(0.96);
    }
    
    &.circle {
        width: 48px;
        min-width: 48px;
        padding: 0;
        border-radius: 50%;
    }
    
    .button-text {
        transition: opacity 0.2s ease, transform 0.2s ease;
        white-space: nowrap;
        
        &.hidden {
            opacity: 0;
            transform: scale(0);
            position: absolute;
        }
    }
}

.design-button {
    background-color: $xw-teal-medium;
    color: white;
    flex: 1;
    min-width: 48px;
    padding: 0 24px;
    transition: all 0.3s ease;

    &.circle {
        flex: none;
        width: 48px;
        padding: 0;
    }
}

.menu-button {
    width: 48px;
    min-width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: $xw-grey-light;
    position: relative;
    transition: all 0.3s ease;
    flex-shrink: 0;

    &.home-active {
        background-color: $xw-white;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    }
    
    .hamburger {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 18px;
        height: 14px;
        
        span {
            display: block;
            width: 100%;
            height: 2px;
            background-color: #333;
            transition: transform 0.3s, opacity 0.2s;
        }
    }
    
    .close {
        color: #333;
        display: flex;
        align-items: center;
        justify-content: center;
        
        svg {
            width: 20px;
            height: 20px;
            stroke: currentColor;
            stroke-width: 2.5;
        }
    }
    
    .home-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #333;
        
        svg {
            width: 20px;
            height: 20px;
            stroke: currentColor;
            stroke-width: 2;
        }
    }
}

.tech-button {
    background-color: $xw-yellow; 
    color: black;
    flex: 1;
    min-width: 48px;
    padding: 0 24px;
    transition: all 0.3s ease;
    
    &.circle {
        flex: none;
        width: 48px;
        padding: 0;
    }
}

// Mobile Menu Content
.mobile-menu {
    position: fixed;
    bottom: 76px;
    left: 50%;
    transform: translateX(-50%) scale(0.9);
    width: 80%;
    max-width: 300px;
    background-color: #fff;
    border-radius: 16px;
    padding: $spacing-lg 0;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
    z-index: 1001;
    opacity: 0;
        transform: translateZ(0);
    -webkit-transform: translateZ(0);
    // iOS-specific transform handling
    -webkit-transform: translateX(-50%) scale(0.9);
    transition: transform 0.3s ease, opacity 0.3s ease, -webkit-transform 0.3s ease;
    
    &::after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%) rotate(45deg);
        -webkit-transform: translateX(-50%) rotate(45deg);
        width: 16px;
        height: 16px;
        background-color: #fff;
        box-shadow: 3px 3px 5px rgba(0, 0, 0, 0.1);
        z-index: -1;
    }
    
    nav {
        width: 100%;
        
        ul {
            list-style: none;
            margin: 0;
            padding: 0;
            
            li {
                text-align: center;
                padding: $spacing-sm 0;
                
                a, button, .nav-link {
                    @extend %body-text-large;
                    display: block;
                    color: #333;
                    text-decoration: none;
                    padding: $spacing-sm $spacing-lg;
                    border-radius: 8px;
                    margin: 0 $spacing-md;
                    cursor: pointer;
                    border: none;
                    background: none;
                    width: calc(100% - #{$spacing-md * 2});
                    text-align: center;
                    transition: all 0.2s;
                    -webkit-tap-highlight-color: transparent;
                    
                    &:hover {
                        color: var(--theme-accent);
                        background-color: rgba(0, 0, 0, 0.05);
                    }
                    
                    &.router-link-exact-active,
                    &.custom-active {
                        @extend %font-bold;
                        color: var(--theme-accent);
                        background-color: rgba(0, 0, 0, 0.05);
                    }
                }

                .nav-link-disabled {
                    @extend %body-text-large;
                    display: block;
                    color: #999;
                    padding: $spacing-sm $spacing-lg;
                    margin: 0 $spacing-md;
                    width: calc(100% - #{$spacing-md * 2});
                    text-align: center;
                    opacity: 0.5;
                    cursor: not-allowed;
                }
            }
        }
    }
}

// Show menu when active (used with v-show)
.navigation-mobile.menu-open .mobile-menu {
    opacity: 1;
    transform: translateX(-50%) scale(1);
    -webkit-transform: translateX(-50%) scale(1);
}

// Safe area inset support for iOS
@supports (padding: max(0px)) {
    .navigation-mobile {
        padding-bottom: max(4px, env(safe-area-inset-bottom, 4px));
        bottom: max(16px, env(safe-area-inset-bottom, 16px));
    }
}

// iOS-specific fixes
@supports (-webkit-touch-callout: none) {
    .navigation-mobile {
        // Use margin centering instead of transform
        transform: none !important;
        -webkit-transform: none !important;
        
        // Create a stacking context
        isolation: isolate;
        
        // Force repaint on scroll
        -webkit-backface-visibility: hidden;
        -webkit-perspective: 1000;
        
        // Single background render
        &::before {
            content: '';
            position: absolute;
            inset: 0;
            background: white;
            border-radius: 28px;
            z-index: -1;
            -webkit-backface-visibility: hidden;
            -webkit-transform: translateZ(0);
        }
        
        // Hide original background on iOS
        background-color: transparent !important;
    }
    
    // Simplify all transforms
    .mobile-menu {
        transform: translate(-50%, 0) scale(0.9);
        -webkit-transform: translate(-50%, 0) scale(0.9);
        
        &.navigation-mobile.menu-open & {
            transform: translate(-50%, 0) scale(1);
            -webkit-transform: translate(-50%, 0) scale(1);
        }
    }
    
    // Additional iOS scroll fix
    body {
        -webkit-transform: translateZ(0);
    }
}

// Disable animations on touch devices to improve performance
@media (hover: none) and (pointer: coarse) {
    .navigation-mobile,
    .nav-button,
    .button-text {
        transition: none !important;
    }
    
    // Keep only essential transitions
    .navigation-mobile {
        transition: max-width 0.3s ease !important;
    }
    
    .mobile-menu {
        transition: opacity 0.2s ease !important;
    }
}

// Reduce motion support
@media (prefers-reduced-motion: reduce) {
    .navigation-mobile,
    .nav-button,
    .mobile-menu,
    .button-text {
        transition: none !important;
        animation: none !important;
    }
}


// When iOS is scrolling
body.ios-scrolling {
  .navigation-mobile {
    // Force GPU repaint
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
  }
}
</style>